import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from './api.service';
import { ConnectAccountDetails, TerminalReader, TerminalSettings } from '../components/terminal-settings/terminal-settings.data';

@Injectable({ providedIn: 'root' })
export class TerminalSettingsService {
  constructor(private api: ApiService) {}

  getConnectAccount(locationId: string): Observable<ConnectAccountDetails | null> {
    const id = String(locationId || '').trim();
    if (!id) return of(null);
    return this.api
      .get<ConnectAccountDetails | null>('location/terminal/connect/account', { location_id: id })
      .pipe(
        map((res) => (res?.status ? (res.data ?? null) : null)),
        catchError(() => of(null))
      );
  }

  createConnectAccount(
    locationId: string,
    payload: { returnUrl: string; refreshUrl: string }
  ): Observable<{ accountId: string; url: string; expiresAt: number } | null> {
    const id = String(locationId || '').trim();
    if (!id) return of(null);
    return this.api
      .post<{ accountId: string; url: string; expiresAt: number }>('location/terminal/connect/account', {
        location_id: id,
        return_url: payload.returnUrl,
        refresh_url: payload.refreshUrl,
      })
      .pipe(
        map((res) => (res?.status && res.data ? res.data : null)),
        catchError(() => of(null))
      );
  }

  createConnectAccountUpdateLink(
    locationId: string,
    payload: { returnUrl: string; refreshUrl: string }
  ): Observable<{ url: string; expiresAt: number } | null> {
    const id = String(locationId || '').trim();
    if (!id) return of(null);
    return this.api
      .post<{ url: string; expiresAt: number }>('location/terminal/connect/account-update-link', {
        location_id: id,
        return_url: payload.returnUrl,
        refresh_url: payload.refreshUrl,
      })
      .pipe(
        map((res) => (res?.status && res.data ? res.data : null)),
        catchError(() => of(null))
      );
  }

  deleteConnectAccount(locationId: string): Observable<boolean> {
    const id = String(locationId || '').trim();
    if (!id) return of(false);
    return this.api
      .delete<any>(`location/terminal/connect/account?location_id=${encodeURIComponent(id)}`)
      .pipe(
        map((res) => !!res?.status),
        catchError(() => of(false))
      );
  }

  listReaders(locationId: string): Observable<TerminalReader[]> {
    const id = String(locationId || '').trim();
    if (!id) return of([]);
    return this.api.get<TerminalReader[]>('location/terminal/readers', { location_id: id }).pipe(
      map((res) => (res?.status && Array.isArray(res.data) ? res.data : [])),
      catchError(() => of([]))
    );
  }

  registerReader(
    locationId: string,
    payload: { registrationCode: string; readerName: string }
  ): Observable<TerminalReader | null> {
    const id = String(locationId || '').trim();
    if (!id) return of(null);
    return this.api
      .post<TerminalReader>('location/terminal/readers', {
        location_id: id,
        registration_code: payload.registrationCode,
        reader_name: payload.readerName,
      })
      .pipe(
        map((res) => (res?.status && res.data ? res.data : null)),
        catchError(() => of(null))
      );
  }

  updateReader(locationId: string, readerId: string, label: string): Observable<TerminalReader | null> {
    const id = String(locationId || '').trim();
    const rid = String(readerId || '').trim();
    if (!id || !rid) return of(null);
    return this.api
      .put<TerminalReader>(`location/terminal/readers/${encodeURIComponent(rid)}`, {
        location_id: id,
        label,
      })
      .pipe(
        map((res) => (res?.status && res.data ? res.data : null)),
        catchError(() => of(null))
      );
  }

  deleteReader(locationId: string, readerId: string): Observable<boolean> {
    const id = String(locationId || '').trim();
    const rid = String(readerId || '').trim();
    if (!id || !rid) return of(false);
    return this.api
      .delete<any>(
        `location/terminal/readers/${encodeURIComponent(rid)}?location_id=${encodeURIComponent(id)}`
      )
      .pipe(
        map((res) => !!res?.status),
        catchError(() => of(false))
      );
  }

  getTerminalSettings(locationId: string): Observable<TerminalSettings | null> {
    const id = String(locationId || '').trim();
    if (!id) return of(null);
    return this.api.get<TerminalSettings>('location/terminal/settings', { location_id: id }).pipe(
      map((res) => (res?.status && res.data ? res.data : null)),
      catchError(() => of(null))
    );
  }

  updateTerminalSettings(
    locationId: string,
    settings: Partial<TerminalSettings>
  ): Observable<TerminalSettings | null> {
    const id = String(locationId || '').trim();
    if (!id) return of(null);
    return this.api
      .put<TerminalSettings>('location/terminal/settings', {
        location_id: id,
        settings: { convenienceFee: settings.convenienceFee },
      })
      .pipe(
        map((res) => (res?.status && res.data ? res.data : null)),
        catchError(() => of(null))
      );
  }

  createTerminalPaymentIntent(
    locationId: string,
    payload: {
      amount: number;
      convenienceFee?: number;
      metadata?: Record<string, string>;
    }
  ): Observable<{
    paymentIntentId: string;
    baseAmountCents: number;
    convenienceFeeCents: number;
    totalAmountCents: number;
  } | null> {
    const id = String(locationId || '').trim();
    if (!id) return of(null);
    return this.api
      .post<{
        paymentIntentId: string;
        baseAmountCents: number;
        convenienceFeeCents: number;
        totalAmountCents: number;
      }>('location/terminal/payment-intent', {
        location_id: id,
        amount: payload.amount,
        convenienceFee: payload.convenienceFee,
        metadata: payload.metadata,
      })
      .pipe(
        map((res) => (res?.status && res.data ? res.data : null)),
        catchError(() => of(null))
      );
  }

  proceedTerminalPayment(
    locationId: string,
    payload: {
      readerId: string;
      paymentIntentId: string;
      totalAmount: number;
      subtotal: number;
      convenienceFee: number;
    }
  ): Observable<any> {
    const id = String(locationId || '').trim();
    if (!id) return of(null);
    return this.api
      .post('location/terminal/payment/proceed', {
        location_id: id,
        reader_id: payload.readerId,
        payment_intent_id: payload.paymentIntentId,
        total_amount: payload.totalAmount,
        subtotal: payload.subtotal,
        convenienceFee: payload.convenienceFee,
      })
      .pipe(
        map((res) => (res?.status ? res.data : null)),
        catchError(() => of(null))
      );
  }
}
